import appEvents from "../utils/appEvents";

export interface AnalyticsForm {
  startTime: number;
  totalTime: number;
  totalTimeFocused: number;
  totalIdleTime: number;
  fillingpercentage: number;
  listCommunicationMethod: CommunicationMethod;
  journey: string[];
  useGoogleMapsServiceCount: number;
  isAddressAutoCompleted: boolean;
  isAddressModifiedAfterAutoCompletion: boolean;
}

export interface CommunicationMethod {
  [fieldName: string]: boolean;
}

export interface AnalyticsFormStep {
  startTime: number;
  endTime: number;
  totalTime: number;
}

export interface AnalyticsField {
  totalTimeFocused: number;
  timeLastFocused: number;
  timeLastBlurred: number;
  isEmpty: boolean;
  isActive: boolean;
  hasError: boolean;
}

export interface AnalyticsFields {
  [fieldName: string]: AnalyticsField;
}

export default class AnalyticsSession {
  form: AnalyticsForm;
  fields: AnalyticsFields;
  salesPersonId: string | null;
  storeId: string | null;
  storeCountry: string | null;
  storeName: string | null;
  countryName: string | null;
  currentStep = 0;
  steps: AnalyticsFormStep[] = [];
  transliteratedField: { [propKey: string]: boolean };

  constructor(
    salesPersonId: string | null,
    storeId: string | null,
    storeCountry: string | null,
    storeName: string | null,
    countryName: string | null
  ) {
    this.form = {
      startTime: +new Date(),
      totalTime: 0,
      totalTimeFocused: 0,
      totalIdleTime: 0,
      fillingpercentage: 0,
      listCommunicationMethod: {
        appsConsent: false,
        emailConsent: false,
        phoneConsent: false,
        postConsent: false
      },
      journey: [],
      useGoogleMapsServiceCount: 0,
      isAddressAutoCompleted: false,
      isAddressModifiedAfterAutoCompletion: false
    };
    this.salesPersonId = salesPersonId;
    this.storeId = storeId;
    this.storeCountry = storeCountry;
    this.storeName = storeName;
    this.countryName = countryName;
    this.startStep(0);
    //Name should be the same as used in the code
    this.fields = {
      honorificPrefix: this.initiateField(),
      givenName: this.initiateField(),
      familyName: this.initiateField(),
      birthDate: this.initiateField(),
      nationality: this.initiateField(),
      email: this.initiateField(),
      telephone: this.initiateField(),
      wechatId: this.initiateField(),
      lineId: this.initiateField(),
      addressCountry: this.initiateField(),
      streetAddress1: this.initiateField(),
      streetAddress2: this.initiateField(),
      streetAddress3: this.initiateField(),
      addressRegion: this.initiateField(),
      addressLocality: this.initiateField(),
      postalCode: this.initiateField()
    };

    this.transliteratedField = {
      globalGivenName: false,
      globalFamilyName: false,
      jpKanaGivenName: false,
      jpKanaFamilyName: false
    };
  }

  startStep(step: number) {
    this.currentStep = step;
    this.steps[step] = this.steps[step] || {
      startTime: +new Date(),
      endTime: 0,
      totalTime: 0
    };
  }

  endStep(step: number) {
    const currentStepInfo = this.steps[step || this.currentStep];
    currentStepInfo.endTime = +new Date();
    currentStepInfo.totalTime =
      currentStepInfo.endTime - currentStepInfo.startTime;
  }

  nextStep(step?: number) {
    const currentStep = step || this.currentStep;
    this.endStep(currentStep);
    this.startStep(currentStep + 1);
  }

  previousStep(step?: number) {
    const currentStep = step || this.currentStep;
    this.endStep(currentStep);
    this.startStep(currentStep - 1);
  }

  appendToJourney(fieldName: string) {
    const lastFieldTouched = this.form.journey.length
      ? this.form.journey[this.form.journey.length - 1]
      : undefined;
    if (!lastFieldTouched || lastFieldTouched !== fieldName) {
      this.form.journey.push(fieldName);
    }
  }

  addGoogleMapsApiUse() {
    this.form.useGoogleMapsServiceCount += 1;
  }

  initiateField() {
    return {
      totalTimeFocused: 0,
      timeLastFocused: 0,
      timeLastBlurred: 0,
      isEmpty: true,
      isActive: false,
      hasError: false
    };
  }

  markFieldNotEmpty(fieldName: string) {
    if (this.fields[fieldName]) {
      this.fields[fieldName].isEmpty = false;
    }
  }

  sendError(fieldName: string) {
    if (this.fields[fieldName] && !this.fields[fieldName].hasError) {
      appEvents.trigger("analytics.error", {
        category: "form_errors",
        action: "form_errors_field",
        label: fieldName,
        fatal: false,
        dimensions: {
          salesPersonId: this.salesPersonId,
          storeId: this.storeId,
          storeCountry: this.storeCountry,
          storeName: this.storeName,
          countryName: this.countryName
        }
      });
      this.fields[fieldName].hasError = true;
    }
  }

  markFieldFocused(fieldName: string) {
    if (this.fields[fieldName] && !this.fields[fieldName].isActive) {
      this.fields[fieldName].timeLastFocused = +new Date();
      this.appendToJourney(fieldName);
      this.fields[fieldName].isActive = true;
    }
  }

  setCommunicationMean(fieldname: string, value: boolean) {
    this.form.listCommunicationMethod[fieldname] = value;
  }
  markFieldBlurred(fieldName: string) {
    if (this.fields[fieldName] && this.fields[fieldName].isActive) {
      const field = this.fields[fieldName];
      field.timeLastBlurred = +new Date();
      const timeFocused = field.timeLastBlurred - field.timeLastFocused;
      field.totalTimeFocused += timeFocused;
      this.fields[fieldName].isActive = false;
    }
  }

  end() {
    this.endStep(2);
    this.form.totalTime = this.steps[2].endTime - this.steps[0].startTime;
    let countFilledField = 0;
    for (let i in this.fields) {
      this.form.totalTimeFocused += this.fields[i].totalTimeFocused;
      if (!this.fields[i].isEmpty) {
        countFilledField += 1;
      }
    }
    this.form.totalIdleTime = this.form.totalTime - this.form.totalTimeFocused;
    this.form.fillingpercentage = Math.floor(
      (countFilledField / Object.keys(this.fields).length) * 100
    );
    const results = {
      form: this.form,
      fields: this.fields,
      dimensions: {
        salesPersonId: this.salesPersonId,
        storeId: this.storeId,
        storeCountry: this.storeCountry,
        storeName: this.storeName,
        countryName: this.countryName
      },
      steps: this.steps
    };

    return results;
  }
}
